import React, { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { UserModalPresenter } from "./UserModal.presenter";
import { BackOfficeUser, AssignedOrgUnits } from "@/domain/user.interface";
import {
  useUserById,
  useUserCreateOne,
  useUserUpdateOne,
  useUserDeleteOne,
  useUserResendInvitation,
} from "@/hooks/useUsers";
import { useSearchRoles } from "@/hooks/management/useRoles.manage";
import {
  useGetPrograms,
  useGetProgram,
} from "@/hooks/management/usePrograms.manage";
import { useCountrySetting } from "@/hooks/useSetting";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { UserStatusEnum } from "@/domain/user.enum";
import validationSchema, { UserFormData } from "./validation.schema";
import { GetIncludes } from "@/services/program.service";
import { GroupedRole } from "./TabRoles";
import { format } from "@react-input/mask";
import AlertDialog from "@/components/AlertDialog";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal";
import { ErrorResponse } from "@/domain/error.interface";
import { ProgramStatusEnum } from "@/domain/programs.enum";
import { Program } from "@/domain/programs.interface";

export interface UserModalContainerProps {
  open: boolean;
  mode: "create" | "edit";
  userId?: string;
  onClose: () => void;
  onSuccess?: () => void;
  canAddUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canResendInvitation: boolean;
}

export const UserModalContainer: FC<UserModalContainerProps> = ({
  open,
  mode,
  userId,
  onClose,
  onSuccess,
  canAddUser,
  canEditUser,
  canDeleteUser,
  canResendInvitation,
}) => {
  const { t } = useTranslation("user");
  const { t: tCommon } = useTranslation("common");
  const { showSnackBar } = useFeedBack();
  const { userInfo, roles: loggedUserRoles } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const countrySetting = useCountrySetting();

  // Fetch user data for edit mode
  const { user, isLoadingUser } = useUserById(
    userId || "",
    open && mode === "edit",
  );

  // Mutations
  const userCreateOne = useUserCreateOne();
  const userUpdateOne = useUserUpdateOne();
  const userDeleteOne = useUserDeleteOne();
  const userResendInvitation = useUserResendInvitation();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
  const [isPermissionDeniedModalOpen, setIsPermissionDeniedModalOpen] =
    useState(false);
  const [selectedProgram, setSelectedProgram] = useState<string>();

  // Reset to first tab when modal opens
  React.useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  // Fetch roles
  const { rolesResponse, isLoadingRoles } = useSearchRoles({
    sortField: "name",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 100,
    search: "",
  });

  // AC01, AC07: Fetch programs - ACTIVE status + previously assigned programs (any status) in edit mode
  const { data: programsData, isLoadingPrograms } = useGetPrograms({
    sortField: "name",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 100,
    search: "",
  });

  // AC07: Get previously assigned program IDs for edit mode
  const previouslyAssignedProgramIds = useMemo(() => {
    if (mode === "edit" && user?.assignedOrgUnits) {
      return user.assignedOrgUnits.map((assigned) => assigned.programId);
    }
    return [];
  }, [mode, user]);

  // AC01, AC07: Filter and combine programs
  const programs = useMemo(() => {
    const allPrograms = programsData?.programs || [];

    // AC01: Filter ACTIVE programs (isDeleted is handled by backend)
    const activePrograms = allPrograms.filter(
      (p) => p.status === ProgramStatusEnum.ACTIVE,
    );

    // AC07: In edit mode, also include previously assigned programs (any status)
    if (mode === "edit" && previouslyAssignedProgramIds.length > 0) {
      const previousPrograms = allPrograms.filter(
        (p) =>
          previouslyAssignedProgramIds.includes(p._id) &&
          !activePrograms.some((ap) => ap._id === p._id),
      );

      // Combine and remove duplicates
      const programsMap = new Map<string, Program>();
      [...activePrograms, ...previousPrograms].forEach((p) => {
        programsMap.set(p._id, p);
      });

      // Sort alphabetically
      return Array.from(programsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    }

    // Create mode: only ACTIVE programs, sorted alphabetically
    return activePrograms.sort((a, b) => a.name.localeCompare(b.name));
  }, [programsData?.programs, mode, previouslyAssignedProgramIds]);

  // Fetch program details when selected (AC02: Get OUs for selected program)
  const { data: programData, isFetchingProgram } = useGetProgram(
    selectedProgram || "",
    selectedProgram ? [GetIncludes.orgUnits] : [],
  );

  // AC02: Filter and sort OUs for selected program
  const organizations = useMemo(() => {
    if (!programData?.orgUnits) return [];

    // AC02: Filter by ACTIVE status and isDeleted=false, sort alphabetically
    return programData.orgUnits
      .filter((ou) => ou.status === "ACTIVE" && !ou.isDeleted)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [programData?.orgUnits]);

  // Form setup
  const methods = useForm<UserFormData>({
    resolver: yupResolver(
      validationSchema(tCommon, countrySetting, mode === "edit"),
    ),
    mode: "all",
    defaultValues: {
      status: UserStatusEnum.PENDING,
      assignedOrgUnits: [],
    },
  });

  // AC01: Categorize roles by type and filter according to logged user
  const categorizedRoles = useMemo(() => {
    const allRoles = rolesResponse?.roles || [];
    const standardSystemRoles: typeof allRoles = [];
    const standardAccountRoles: typeof allRoles = [];
    const customRoles: typeof allRoles = [];

    // Get logged user's role names
    const loggedUserRoleNames =
      loggedUserRoles?.map((r) => r.name?.toLowerCase() || "") || [];

    // Check if user has System Admin, Account Admin, or Support role
    const isSystemAdmin = loggedUserRoleNames.some(
      (name) => name === "system admin" || name.includes("system admin"),
    );
    const isAccountAdmin = loggedUserRoleNames.some(
      (name) => name === "account admin" || name.includes("account admin"),
    );
    const isSupport = loggedUserRoleNames.some(
      (name) => name === "support" || name.includes("support"),
    );

    allRoles.forEach((role) => {
      // role.type is a string from the API, not the enum
      const roleType = role.type as string;
      const roleName = (role.name || "").toLowerCase();

      // Match COYN types: STANDARD_SYSTEM_ROLE, STANDARD_ACCOUNT_ROLE, CUSTOM_ROLE
      if (roleType === "STANDARD_SYSTEM_ROLE") {
        // AC01: Filter standard system roles based on logged user
        if (isSystemAdmin) {
          // System Admin sees all: Support, Account Admin, System Admin
          standardSystemRoles.push(role);
        } else if (isAccountAdmin || isSupport) {
          // Account Admin or Support see only Account Admin
          if (
            roleName === "account admin" ||
            roleName.includes("account admin")
          ) {
            standardSystemRoles.push(role);
          }
        }
        // Other roles don't see standard system roles
      } else if (roleType === "STANDARD_ACCOUNT_ROLE") {
        // Standard account roles - always visible
        standardAccountRoles.push(role);
      } else if (roleType === "CUSTOM_ROLE") {
        customRoles.push(role);
      }
    });

    return {
      standardSystemRoles: standardSystemRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
      standardAccountRoles: standardAccountRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
      customRoles: customRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
    };
  }, [rolesResponse?.roles, loggedUserRoles]);

  // Convert to GroupedRole format for TabRoles
  const rolesArray = useMemo(() => {
    const groups: GroupedRole[] = [];

    if (categorizedRoles.standardSystemRoles.length > 0) {
      groups.push({
        title: "Standard system roles",
        roles: categorizedRoles.standardSystemRoles.map((r) => ({
          _id: r._id,
          name: r.name,
          type: r.type,
        })),
      });
    }

    if (categorizedRoles.standardAccountRoles.length > 0) {
      groups.push({
        title: "Standard account roles",
        roles: categorizedRoles.standardAccountRoles.map((r) => ({
          _id: r._id,
          name: r.name,
          type: r.type,
        })),
      });
    }

    if (categorizedRoles.customRoles.length > 0) {
      groups.push({
        title: "Custom roles",
        roles: categorizedRoles.customRoles.map((r) => ({
          _id: r._id,
          name: r.name,
          type: r.type,
        })),
      });
    }

    return groups;
  }, [categorizedRoles]);

  // Initialize form data when user is loaded (edit mode)
  useEffect(() => {
    if (mode === "edit" && user && !isLoadingUser && open) {
      methods.reset({
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        phoneNumber: format(user?.phoneNumber ?? "", {
          mask: "____ ______",
          replacement: { _: /\d/ },
        }),
        npi: user?.npi ?? "",
        roleId:
          user?.roleIds && user?.roleIds?.length > 0 ? user?.roleIds[0] : "",
        assignedOrgUnits: user?.assignedOrgUnits ?? [],
        status: (user?.status as UserStatusEnum) ?? UserStatusEnum.PENDING,
      });
      // Set first program if user has assigned org units
      if (user?.assignedOrgUnits && user.assignedOrgUnits.length > 0) {
        setSelectedProgram(user.assignedOrgUnits[0].programId);
      }
    } else if (mode === "create" && open) {
      methods.reset({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        npi: "",
        roleId: "",
        assignedOrgUnits: [],
        status: UserStatusEnum.PENDING,
      });
      setSelectedProgram(undefined);
      setActiveTab(0);
    }
  }, [mode, user, isLoadingUser, open, methods]);

  // AC01: Set default program to first program when programs load (create mode)
  useEffect(() => {
    if (mode === "create" && programs.length > 0 && !selectedProgram && open) {
      setSelectedProgram(programs[0]._id);
    }
  }, [programs, selectedProgram, mode, open]);

  // AC07: Pre-select first program from user's assigned programs in edit mode
  useEffect(() => {
    if (
      mode === "edit" &&
      user &&
      !isLoadingUser &&
      open &&
      user.assignedOrgUnits &&
      user.assignedOrgUnits.length > 0 &&
      !selectedProgram
    ) {
      setSelectedProgram(user.assignedOrgUnits[0].programId);
    }
  }, [mode, user, isLoadingUser, open, selectedProgram]);

  // Check if selected role is account admin, sysadmin, or support (AC04, AC06)
  const watchedFormData = methods.watch();
  const selectedRoleId = watchedFormData.roleId;
  const selectedRole = useMemo(() => {
    if (!selectedRoleId || !rolesResponse?.roles) return null;
    return rolesResponse.roles.find((r) => r._id === selectedRoleId);
  }, [selectedRoleId, rolesResponse?.roles]);

  const isAccountAdmin = useMemo(() => {
    if (!selectedRole?.name) return false;
    const roleName = selectedRole.name.toLowerCase();
    return roleName === "account admin" || roleName.includes("account admin");
  }, [selectedRole]);

  const isSystemAdmin = useMemo(() => {
    if (!selectedRole?.name) return false;
    const roleName = selectedRole.name.toLowerCase();
    return roleName === "system admin" || roleName.includes("system admin");
  }, [selectedRole]);

  const isSupport = useMemo(() => {
    if (!selectedRole?.name) return false;
    const roleName = selectedRole.name.toLowerCase();
    return roleName === "support" || roleName.includes("support");
  }, [selectedRole]);

  const isExemptFromOUValidation = isAccountAdmin || isSystemAdmin || isSupport;

  // Handle tab change
  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    if (newValue === 2) {
      const isValidTabRole = await methods.trigger("roleId");
      if (!isValidTabRole) {
        setActiveTab(1);
        showSnackBar(
          t("messages.missing-role") || "You must select a role first",
          "error",
        );
        return;
      }
      setActiveTab(newValue);
    } else {
      setActiveTab(newValue);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (mode === "create" && !canAddUser) {
      showSnackBar(tCommon("generic_not_allowed") || "Not allowed", "error");
      return;
    }
    if (mode === "edit" && !canEditUser) {
      showSnackBar(tCommon("generic_not_allowed") || "Not allowed", "error");
      return;
    }

    // AC04: Validate minimum OU selection (except for account admin, sysadmin, support)
    const assignedOrgUnits = (methods.getValues("assignedOrgUnits") ||
      []) as AssignedOrgUnits[];
    if (!isExemptFromOUValidation) {
      const totalSelectedOUs = assignedOrgUnits.reduce(
        (sum: number, assigned: AssignedOrgUnits) =>
          sum + (assigned.orgUnitIds?.length || 0),
        0,
      );
      if (totalSelectedOUs === 0) {
        methods.setError("assignedOrgUnits", {
          type: "manual",
          message:
            "At least one organizational unit must be selected for this user.",
        });
      } else {
        methods.clearErrors("assignedOrgUnits");
      }
    } else {
      // Clear OU validation error for exempt roles
      methods.clearErrors("assignedOrgUnits");
    }

    // AC12: Navigate to first tab with errors
    const isValid = await methods.trigger();
    if (!isValid) {
      const errors = methods.formState.errors;

      // Check which tab has errors (in order: User tab = 0, User Role tab = 1, Assign OU tab = 2)
      const userTabErrors = [
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "npi",
        "status",
      ].some((field) => errors[field as keyof typeof errors]);

      const roleTabErrors = !!errors.roleId;
      const ouTabErrors = !!errors.assignedOrgUnits;

      if (userTabErrors) {
        setActiveTab(0);
      } else if (roleTabErrors) {
        setActiveTab(1);
      } else if (ouTabErrors) {
        setActiveTab(2);
      }

      // AC04, AC10: Only show validation messages in fields, no snackbar
      return;
    }

    const formDataValues = methods.getValues();
    const payload: BackOfficeUser = {
      firstName: formDataValues.firstName as string,
      lastName: formDataValues.lastName as string,
      email: formDataValues.email as string,
      phoneNumber: formDataValues.phoneNumber as string,
      npi: formDataValues.npi as string,
      status:
        (formDataValues.status as UserStatusEnum) || UserStatusEnum.PENDING,
      roleIds: [formDataValues.roleId] as string[],
      assignedOrgUnits: (formDataValues.assignedOrgUnits ||
        []) as AssignedOrgUnits[],
      createdBy: mode === "create" ? userInfo?._id : undefined,
      updatedBy: mode === "edit" ? userInfo?._id : undefined,
    };

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        await userCreateOne.mutateAsync(payload);
        showSnackBar(
          t("messages.add-success") || "User created successfully",
          "success",
        );

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.USERS,
          option: OPTIONS.USER_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_USRS_USR_ADD_INVITE,
          action: "When inviting a new user (adding)",
          detail: `Invited new user ${formDataValues.email}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      } else {
        await userUpdateOne.mutateAsync({
          userId: userId ?? "",
          user: payload,
        });
        showSnackBar(
          t("messages.update-success") || "User updated successfully",
          "success",
        );

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.USERS,
          option: OPTIONS.USER_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_USRS_USR_EDIT,
          action: "When saving a user (editing)",
          detail: `Edited user ${formDataValues.email}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as any;

      // Extract error message from various possible structures
      let errorMessage = "An error occurred";

      // Handle nested error structure from API: error.errorMessage.errorMessage
      if (err.error?.errorMessage?.errorMessage) {
        errorMessage = String(err.error.errorMessage.errorMessage);
      } else if (
        err.error?.errorMessage &&
        typeof err.error.errorMessage === "string"
      ) {
        errorMessage = err.error.errorMessage;
      } else if (err.cause?.error?.errorMessage) {
        errorMessage = String(err.cause.error.errorMessage);
      } else if (Array.isArray(err.message)) {
        errorMessage = String(err.message[0] || "An error occurred");
      } else if (typeof err.message === "string") {
        errorMessage = err.message;
      } else if (typeof err.error === "string") {
        errorMessage = err.error;
      } else if (typeof err.errorMessage === "string") {
        errorMessage = err.errorMessage;
      } else if (err.response?.data?.error?.errorMessage?.errorMessage) {
        errorMessage = String(
          err.response.data.error.errorMessage.errorMessage,
        );
      } else if (err.response?.data?.errorMessage) {
        errorMessage = String(err.response.data.errorMessage);
      } else if (err.response?.data?.message) {
        errorMessage = String(err.response.data.message);
      }

      // AC05: Check for duplicate email error
      const errorMessageLower = errorMessage.toLowerCase();
      const isDuplicateEmail =
        errorMessageLower.includes("email") &&
        (errorMessageLower.includes("already") ||
          errorMessageLower.includes("duplicate") ||
          errorMessageLower.includes("exist") ||
          errorMessageLower.includes("in use"));

      if (isDuplicateEmail) {
        showSnackBar(t("messages.duplicate-email"), "error");
      } else {
        showSnackBar(errorMessage, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // AC00-B, AC01: Handle delete click - check permission and open dialog
  const handleDeleteClick = () => {
    // AC00-B: Check permission
    if (!canDeleteUser) {
      setIsPermissionDeniedModalOpen(true);
      return;
    }
    // AC01: Open delete confirmation dialog
    setIsDeleteDialogOpen(true);
  };

  // AC02: Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!userId || !user) return;

    try {
      const userFullName =
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email || "user";

      await userDeleteOne.mutateAsync({ userId });

      // AC00-A: Log WEB_USRS_USR_DELETE
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.USERS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_USRS_USR_DELETE,
        action: "When deleting a user",
        detail: `Deleted the user ${userFullName}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });

      // AC02: Show success message, close dialog and modal, refresh list
      showSnackBar("The registry has been deleted.", "success");
      setIsDeleteDialogOpen(false);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error",
      );
      // Keep dialog open on error so user can retry or cancel
    }
  };

  // AC03: Handle delete cancel
  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  // AC00-B, AC01: Handle resend invitation click - check permission and open dialog
  const handleResendInvitationClick = () => {
    // AC00-B: Check permission
    if (!canResendInvitation) {
      setIsPermissionDeniedModalOpen(true);
      return;
    }

    // AC01: Check if user is in PENDING status
    if (user?.status !== UserStatusEnum.PENDING) {
      // Should not happen if UI is correct, but add safety check
      return;
    }

    // AC01: Open resend confirmation dialog
    setIsResendDialogOpen(true);
  };

  // AC02: Handle resend invitation confirm
  const handleResendInvitationConfirm = async () => {
    if (!userId || !user) return;

    try {
      const userFullName =
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email || "user";

      await userResendInvitation.mutateAsync(user.email);

      // AC00-A: Log WEB_USRS_USR_RESEND_LINK
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.USERS,
        option: OPTIONS.RESEND_VERIFICATION_LINK,
        actionCode: ACTION_CODE.WEB_USRS_USR_RESEND_LINK,
        action: "When resending link",
        detail: `Resend verification link for ${userFullName}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });

      // AC02: Show success message and close dialog (user modal stays open)
      showSnackBar(
        "A new verification link has been sent to the user",
        "success",
      );
      setIsResendDialogOpen(false);
    } catch (error) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error",
      );
      // Keep dialog open on error so user can retry or cancel
    }
  };

  // AC03: Handle resend invitation cancel
  const handleResendInvitationCancel = () => {
    setIsResendDialogOpen(false);
  };

  return (
    <>
      <FormProvider {...methods}>
        <UserModalPresenter
          open={open}
          mode={mode}
          user={user || null}
          isLoadingUserData={isLoadingUser}
          formData={watchedFormData}
          activeTab={activeTab}
          isSubmitting={
            isSubmitting || userCreateOne.isPending || userUpdateOne.isPending
          }
          onClose={onClose}
          onTabChange={(event, newValue) => handleTabChange(event, newValue)}
          onSubmit={handleSubmit}
          onResendInvitation={handleResendInvitationClick}
          onDelete={handleDeleteClick}
          canResendInvitation={canResendInvitation}
          rolesArray={rolesArray}
          isLoadingRoles={isLoadingRoles}
          // TabAssignOU props
          programs={programs}
          isLoadingPrograms={isLoadingPrograms}
          selectedProgram={selectedProgram}
          onProgramChange={setSelectedProgram}
          organizations={organizations}
          isLoadingOrganizations={isFetchingProgram}
          isAccountAdmin={isAccountAdmin}
          isSystemAdmin={isSystemAdmin}
          isSupport={isSupport}
          isExemptFromOUValidation={isExemptFromOUValidation}
        />
      </FormProvider>

      {/* AC01, AC02, AC03: Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        title="Delete"
        description="Are you sure you want to delete this registry?"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmColor="error"
      />

      {/* AC01, AC02, AC03: Resend Invitation Dialog */}
      <AlertDialog
        open={isResendDialogOpen}
        title="Resend invitation link"
        description="Are you sure you want to resend the invitation link?"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleResendInvitationConfirm}
        onCancel={handleResendInvitationCancel}
        confirmColor="primary"
      />

      {/* Permission Denied Modal */}
      <PermissionDeniedModal
        open={isPermissionDeniedModalOpen}
        onClose={() => setIsPermissionDeniedModalOpen(false)}
      />
    </>
  );
};
